import { type SupportedLocale } from "../i18n-locales";
import { type LocaleAlternate } from "./routes";

// Canonical production domain for Enkey Agency. Used to build absolute URLs
// for hreflang alternates and structured data. When the app runs on another
// host (preview / staging) the live browser origin is used for
// canonical/OG so links stay clickable, while hreflang/JSON-LD use this base.
export const SITE_URL = "https://www.enkey.eu";
export const SITE_NAME = "Enkey Agency";
export const SITE_LOCALE_DEFAULT: SupportedLocale = "it";

// Organisation facts (kept in one place, reused by JSON-LD).
export const ORG = {
  name: "Enkey Agency",
  legalName: "ENKEY SNC",
  email: "info@enkey.eu",
  telephone: "+39 0781 62719",
  vatId: "IT03202450924",
  rea: "CA253701",
  duns: "432290442",
  founder: "Nicola Cossu",
  professionalExperienceSince: "1999",
  street: "Via Gramsci 153",
  postalCode: "09013",
  city: "Carbonia",
  region: "SU",
  country: "IT",
  logo: "https://www.enkey.eu/brand/enkey-logo.png",
} as const;

// Map an app locale to an OpenGraph locale tag (e.g. it -> it_IT).
export function ogLocale(locale: SupportedLocale): string {
  const region: Record<SupportedLocale, string> = {
    it: "it_IT",
    en: "en_GB",
    es: "es_ES",
    fr: "fr_FR",
    de: "de_DE",
  };
  return region[locale];
}

// Build an absolute URL from a path using the production domain.
export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path}`;
}

/** Word-safe title composer for dynamic records with unpredictable names. */
export function composeSeoTitle(
  primary: string,
  suffix: string,
  maximumLength = 60,
): string {
  const cleanPrimary = primary.replace(/\s+/g, " ").trim();
  const cleanSuffix = suffix.replace(/\s+/g, " ").trim();
  const separator = cleanSuffix ? " — " : "";
  const reserved = separator.length + cleanSuffix.length;
  if (cleanPrimary.length + reserved <= maximumLength) {
    return `${cleanPrimary}${separator}${cleanSuffix}`;
  }
  const available = Math.max(12, maximumLength - reserved - 1);
  const candidate = cleanPrimary.slice(0, available + 1);
  const wordEnd = candidate.lastIndexOf(" ");
  const truncated = candidate.slice(0, Math.max(wordEnd, available)).trim();
  return `${truncated}…${separator}${cleanSuffix}`.slice(0, maximumLength);
}

// Build a useful, page-specific meta description when a CMS summary is too
// short on its own. Google may truncate snippets depending on the query and
// device, so this keeps the copy natural while targeting a compact 140-155
// character range instead of padding with keywords.
export function composeMetaDescription(
  primary: string,
  supporting = "",
  minimumLength = 140,
  maximumLength = 155,
): string {
  const clean = (value: string) => value.replace(/\s+/g, " ").trim();
  const first = clean(primary);
  const second = clean(supporting);
  const combined =
    second && !first.toLocaleLowerCase().includes(second.toLocaleLowerCase())
      ? clean(`${first} ${second}`)
      : first;
  if (combined.length <= maximumLength) return combined;

  const completeSentences = combined.match(/[^.!?]+[.!?]+/g) ?? [];
  let sentenceCandidate = "";
  for (const sentence of completeSentences) {
    const next = clean(`${sentenceCandidate} ${sentence}`);
    if (next.length > maximumLength) break;
    sentenceCandidate = next;
  }
  if (sentenceCandidate.length >= minimumLength) {
    return sentenceCandidate;
  }

  // Keep a complete primary sentence only when it is already long enough.
  // Otherwise continue into the supporting copy and mark the word-safe cut
  // with an ellipsis: this avoids both thin snippets and fake endings such as
  // "su misura per la.".
  if (
    first.length >= minimumLength &&
    first.length <= maximumLength &&
    /[.!?]$/.test(first)
  ) {
    return first;
  }

  const candidate = combined.slice(0, maximumLength);
  const wordEnd = candidate.lastIndexOf(" ");
  const trimmed = candidate.slice(0, Math.max(wordEnd, 1)).trim();
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}…`;
}

/**
 * Use curated page copy while preserving a genuine custom value entered in
 * the CRM. Older seeded rows stored the public summary verbatim in
 * `metaDescription`; those defaults can adopt the improved localized copy
 * without overwriting later editorial changes.
 */
export function resolveServiceMetaDescription(input: {
  catalogMetaDescription?: string;
  summary: string;
  supporting?: string;
  curated?: string;
}): string {
  const clean = (value: string | undefined) =>
    value?.replace(/\s+/g, " ").trim() ?? "";
  const catalog = clean(input.catalogMetaDescription);
  const summary = clean(input.summary);
  const curated = clean(input.curated);
  const customCatalog = catalog && catalog !== summary ? catalog : "";
  const primary = customCatalog || curated || catalog || summary;
  return composeMetaDescription(primary, clean(input.supporting));
}

/** Localized service title without a city or region constraint. */
export function localizedServiceSeoTitle(serviceTitle: string, _locale: SupportedLocale): string {
  const clean = serviceTitle.replace(/\s+/g, " ").trim();
  return composeSeoTitle(clean, "Enkey", 60);
}

/** Keep service descriptions useful without appending geographic targeting. */
export function localizedServiceMetaDescription(input: { serviceTitle: string; locale: SupportedLocale; description: string }): string {
  const current = input.description.replace(/\s+/g, " ").trim();
  return composeMetaDescription(current || input.serviceTitle, "", 140, 155);
}

// hreflang alternates including x-default (points to the default locale).
export function hreflangAlternates(
  alternates: LocaleAlternate[],
  xDefaultPath?: string,
): {
  hreflang: string;
  href: string;
}[] {
  const tags: { hreflang: string; href: string }[] = alternates.map((alt) => ({
    hreflang: alt.locale,
    href: absoluteUrl(alt.path),
  }));
  const def =
    alternates.find((a) => a.locale === SITE_LOCALE_DEFAULT) ?? alternates[0];
  if (xDefaultPath || def) {
    tags.push({
      hreflang: "x-default",
      href: absoluteUrl(xDefaultPath ?? def!.path),
    });
  }
  return tags;
}
